import { useMemo } from 'react';
import { useCurrency } from '../../context/CurrencyContext';

/**
 * Shared number formatters for the Split Buy / Split Sell planners. Values are
 * assumed to already be in the selected display currency (not USD).
 *
 *   fmtCoins  coin amounts, up to 4 dp (never 6-dp junk like 333.333333)
 *   fmtPrice  prices, 2–4 dp (0.923077 -> 0.9231)
 *   fmtTotal  currency totals, exactly 2 dp
 */
export function usePlannerFormat() {
  const { currency } = useCurrency();
  return useMemo(() => {
    const code = currency.code;

    const fmtCoins = (amount: number): string => {
      if (!isFinite(amount) || amount === 0) return '0';
      if (amount > 0 && amount < 0.0001) return amount.toExponential(4);
      return amount.toLocaleString('en-US', { maximumFractionDigits: 4 });
    };

    const fmtPrice = (amount: number): string => {
      try {
        return new Intl.NumberFormat('en-US', {
          style: 'currency', currency: code,
          minimumFractionDigits: 2, maximumFractionDigits: 4,
        }).format(amount);
      } catch {
        return `${code} ${amount.toFixed(4)}`;
      }
    };

    const fmtTotal = (amount: number): string => {
      try {
        return new Intl.NumberFormat('en-US', {
          style: 'currency', currency: code,
          minimumFractionDigits: 2, maximumFractionDigits: 2,
        }).format(amount);
      } catch {
        return `${code} ${amount.toFixed(2)}`;
      }
    };

    return { fmtCoins, fmtPrice, fmtTotal };
  }, [currency.code]);
}
